import React, { Component } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
	Send,
	CheckCircle,
	Clock,
	XCircle,
	AlertCircle,
	Circle,
	RotateCcw,
} from 'lucide-react';

import {
	stripeAccountConnected,
	fetchCustomerRecentInvoices,
} from '../../../../actions/payments';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { useIsMobile } from '@/hooks/use-mobile';
import { DetailsSectionActions, DetailsSectionHeader } from './DetailsSection';

class PaymentsInfo extends Component {
	state = { loading: false };

	componentDidMount() {
		if (this.isEligibleToShowPayments()) {
			this.getCustomerInvoices();
		}
	}

	componentDidUpdate(prevProps) {
		const prevCustomerId = prevProps.customer?._id;
		const nextCustomerId = this.props.customer?._id;

		if (prevCustomerId !== nextCustomerId && this.isEligibleToShowPayments()) {
			this.getCustomerInvoices();
		}
	}

	isEligibleToShowPayments = () => {
		const auth = this.props.auth;
		const accessLevel = auth?.user?.accessLevel;
		const userControlLevels = [
			'owner',
			'location-admin',
			'team-leader',
			'team-member',
			'contributor',
		];

		return Boolean(
			auth?.products &&
				auth.products.includes('payments') &&
				auth.stripeAccountIsConnected &&
				this.props.hasStripeAccount &&
				userControlLevels.includes(accessLevel),
		);
	};

	getCustomerInvoices = async () => {
		try {
			this.setState({ loading: true });

			const { _id: _customer } = this.props.customer;

			await this.props.fetchCustomerRecentInvoices(_customer);

			this.setState({
				loading: false,
			});
		} catch {
			this.setState({ loading: false });
		}
	};

	// Desktop opens payments in a new tab beside the inbox. Phones have no
	// tabs to come back from, so they open in place and Back returns here.
	openPath = (path) => {
		if (this.props.isMobile && this.props.navigate) {
			this.props.navigate(path);
			return;
		}

		window.open(path, '_blank');
	};

	showPayment = (invoice) => {
		if (invoice.type === 'runner_invoice') {
			// runner invoice
			// link is on this dashboard. at /payments/details/works/${_invoice}
			this.openPath(`/payments/details/works/${invoice._invoice}`);
			return;
		}

		// stripe invoice
		// link is on this dashboard. at /payments/details/pay/${_invoice}
		this.openPath(`/payments/details/pay/${invoice._invoice}`);
	};

	showRecentPayments = () => {
		const { _id, name, phone, email } = this.props.customer || {};

		if (_id) {
			const customerName =
				name?.trim() || phone || email || 'Customer';

			this.openPath(
				`/payments?customer=${_id}&customerName=${encodeURIComponent(customerName)}`,
			);
			return;
		}

		this.openPath('/payments');
	};

	getRecentPaymentsHeaderCount = () => {
		const customerInvoices = Array.isArray(this.props.customerInvoices)
			? this.props.customerInvoices
			: [];

		return Math.min(customerInvoices.length, 3);
	};

	getPaymentRowLabel = (invoice) => {
		const description = invoice.description?.trim();
		if (description) {
			return description;
		}

		const customerName = invoice.customer?.name?.trim();
		if (customerName) {
			return customerName;
		}

		return '';
	};

	renderRecentPayments = () => {
		const customerInvoices = Array.isArray(this.props.customerInvoices)
			? this.props.customerInvoices
			: [];

		if (this.state.loading) {
			return (
				<div className="flex items-center justify-center py-3">
					<Loader2 className="size-4 animate-spin text-muted-foreground" />
				</div>
			);
		}

		if (customerInvoices.length === 0) {
			return (
				<p className="py-2 text-xs text-muted-foreground">
					No recent payments.
				</p>
			);
		}

		return (
			<ul className="divide-y divide-border overflow-hidden rounded-md border border-border/60">
				{customerInvoices.slice(0, 3).map((invoice) => {
					const label = this.getPaymentRowLabel(invoice);

					return (
						<li key={invoice.id} className="min-w-0">
							<button
								type="button"
								onClick={() => this.showPayment(invoice)}
								title={
									label
										? `${invoice.amountFormatted} — ${label}`
										: invoice.amountFormatted
								}
								className="group grid w-full min-w-0 grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-x-2 px-2 py-1.5 text-left transition-colors hover:bg-muted/40 max-md:min-h-11 max-md:px-3 max-md:py-2 max-md:active:bg-muted/60"
							>
								<span className="shrink-0">
									{this.getStatusIcon(
										invoice.statusFormatted,
									)}
								</span>
								<span className="shrink-0 truncate text-sm font-medium tabular-nums text-foreground group-hover:underline">
									{invoice.amountFormatted}
								</span>
								{label ? (
									<span
										className="truncate text-xs text-muted-foreground"
										title={label}
									>
										{label}
									</span>
								) : (
									<span aria-hidden="true" />
								)}
								<span
									className="shrink-0 truncate text-right text-[11px] text-muted-foreground"
									title={invoice.createdFormatted}
								>
									{invoice.createdFormatted}
								</span>
							</button>
						</li>
					);
				})}
			</ul>
		);
	};

	getStatusIcon = (status) => {
		switch (status) {
			case 'Sent':
				return <Send className="size-3" style={{ color: '#5469D4' }} />;
			case 'Paid':
				return (
					<CheckCircle
						className="size-3"
						style={{ color: '#0043CE' }}
					/>
				);
			case 'Canceled':
				return (
					<XCircle className="size-3" style={{ color: '#BA4E00' }} />
				);
			case 'Overdue':
				return (
					<AlertCircle
						className="size-3"
						style={{ color: '#DA1E28' }}
					/>
				);
			case 'Pending approval':
				return (
					<Clock className="size-3" style={{ color: '#FFC107' }} />
				);
			case 'Draft':
				return (
					<Circle className="size-3" style={{ color: '#6F6F6F' }} />
				);
			case 'Refunded':
				return (
					<RotateCcw className="size-3" style={{ color: '#000' }} />
				);
			case 'Partial Refund':
				return (
					<RotateCcw className="size-3" style={{ color: '#000' }} />
				);
			default:
				return (
					<Circle className="size-3" style={{ color: '#6F6F6F' }} />
				);
		}
	};

	render() {
		const { accessLevel } = this.props.auth.user;
		const userControlLevels = [
			'owner',
			'location-admin',
			'team-leader',
			'team-member',
			'contributor',
		];

		if (!this.props.auth.products) {
			return null;
		}

		// Render the section as soon as it's eligible, and let the inner content
		// handle loading / empty states. This avoids layout shift when invoices
		// load asynchronously.
		if (this.isEligibleToShowPayments()) {
			const customerInvoices = Array.isArray(
				this.props.customerInvoices,
			)
				? this.props.customerInvoices
				: [];
			const showViewAll =
				!this.state.loading && customerInvoices.length > 0;

			return (
				<div
					key={this.props.customer._id}
					className="border-b border-border/60"
				>
					<Collapsible>
						<DetailsSectionHeader
							label="Recent payments"
							title={
								<>
									Recent payments (
									{this.getRecentPaymentsHeaderCount()})
								</>
							}
							actions={
								showViewAll && (
									<DetailsSectionActions>
										<Button
											variant="link"
											size="sm"
											className="h-5 gap-0.5 px-0 py-0 text-xs font-medium text-muted-foreground max-md:h-11 max-md:px-2 max-md:text-sm"
											onClick={this.showRecentPayments}
										>
											View all
											<ArrowUpRight className="size-3 max-md:hidden" />
										</Button>
									</DetailsSectionActions>
								)
							}
						/>
						<CollapsibleContent>
							<div className="px-4 pb-2.5 pt-0">
								{this.renderRecentPayments()}
							</div>
						</CollapsibleContent>
					</Collapsible>
				</div>
			);
		}

		return <></>;
	}
}

function mapStateToProps(state) {
	return {
		hasStripeAccount: state.payments.hasStripeAccount,
		customerInvoices: state.payments.customerInvoices,
		auth: state.auth,
	};
}

const ConnectedPaymentsInfo = connect(mapStateToProps, {
	stripeAccountConnected,
	fetchCustomerRecentInvoices,
})(PaymentsInfo);

export default function PaymentsInfoSection(props) {
	const isMobile = useIsMobile();
	const navigate = useNavigate();

	return <ConnectedPaymentsInfo {...props} isMobile={isMobile} navigate={navigate} />;
}
